


















import OpenStreetMapLayer from '@/components/maps/OpenStreetMapLayer.vue';
import {limits, scale} from 'chroma-js';
import {GeoJSON, GeoJSONOptions} from 'leaflet';
import {PropType} from 'vue';
import {Vue} from 'vue-property-decorator';
import {LGeoJson, LLayerGroup, LMap, LTileLayer} from 'vue2-leaflet';
import AreaTooltip from './AreaTooltip.vue';
import CurrencyPicker from './CurrencyPicker.vue';
import EstateTypeControl from './EstateTypeControl.vue';

interface AreaPrice {
    amount: number;
    currency: string;
}

interface AreaGeometry {
    id: string;
    name: string;
    geometry: object | any[];
}

interface AreaWithPrice extends AreaGeometry {
    price: AreaPrice | null;
}

interface ColoredArea extends AreaWithPrice {
    color: string;
}

export default Vue.extend({
    components: {OpenStreetMapLayer, LMap, LTileLayer, LGeoJson, EstateTypeControl, LLayerGroup, CurrencyPicker},

    props: {
        countries: Array as PropType<string[]>,
        width: {
            type: String,
            default: '1110px',
        },
        height: {
            type: String,
            default : '600px',
        },
        estateTypes: {
            type: Array as PropType<string[]>,
            default: ['flat'],
        },
        showPickers: {
            type: Boolean,
            default: true,
        },
        areasEndpoint: String,
        pricesEndpoint: String,
        currencies: Array as PropType<string[]>,
    },

    mounted() {
        this.reload();
    },

    data() {
        return {
            map: null,
            areaGeometries: {} as { [countryCode: string]: AreaGeometry[] },
            areaPrices: {} as { [cacheKey: string]: { [placeId: string ]: AreaPrice } },
            areasByCountry: {} as { [countryCode: string]: ColoredArea[] },
            areasById: {},
            fillOpacity: 0.5,
            bounds: [[51.48822432632349, 23.367919921875004], [47.19717795172789, 11.173095703125]],
            estateType: this.estateTypes[0],
            currency: this.currencies[0],
            loaded: false,
        };
    },
    methods: {
        async reload() {
            this.loaded = false;
            await Promise.all(this.countries.map(code => this.loadCountry(code)));
            this.loaded = true;
        },
        async loadCountry(countryCode: string): Promise<void> {
            const [areas, prices] = await Promise.all([
                this.loadAreas(countryCode),
                this.loadPrices(countryCode),
            ]);

            this.renderAreas(
                areas.map(area => ({
                    ...area,
                    price: prices[area.id] || null,
                })),
                countryCode,
            );
        },
        async loadAreas(countryCode: string): Promise<AreaGeometry[]> {
            if (this.areaGeometries.hasOwnProperty(countryCode)) {
                return this.areaGeometries[countryCode];
            }

            const response = await fetch(this.areasEndpoint.replace('__country__', countryCode));

            return this.areaGeometries[countryCode] = await response.json();
        },

        async loadPrices(countryCode: string) {
            const cacheKey = countryCode + this.estateType + this.currency;

            if (this.areaPrices.hasOwnProperty(cacheKey)) {
                return this.areaPrices[cacheKey];
            }

            const response = await fetch(
                this.pricesEndpoint
                    .replace('__country__', countryCode)
                    .replace('__currency__', this.currency)
                    .replace('__estateType__', this.estateType),
            );

            return this.areaPrices[cacheKey] = await response.json();
        },
        renderAreas(areas: AreaWithPrice[], countryCode: string) {
            const prices = areas
                .map(area => area.price)
                .filter((price): price is AreaPrice => price !== null && price.amount > 0)
                .map(price => price.amount)
                .sort()
                .reverse();

            const colors = scale(['green', 'yellow', 'red']).mode('lch').colors(15);
            const colorScale = scale(colors).domain(limits(prices, 'l', 14));

            const areasWithColors = areas.map(area => {
                const color = area.price === null || area.price.amount <= 0
                    ? '#808080'
                    : colorScale(area.price.amount).hex();

                return {
                    ...area,
                    color,
                    id: area.id + this.estateType + (area.price !== null ? area.price.currency : ''),
                };
            });

            this.$set(this.areasByCountry, countryCode, areasWithColors);
        },

        getPolygonStyle(area: ColoredArea): GeoJSONOptions {
            const fillOpacity = this.fillOpacity;

            return {
                weight: 1,
                color: '#808080',
                opacity: 0.75,
                fillColor: area.color,
                fillOpacity,
                onEachFeature: (feature, layer) => {
                    layer.on('mouseover', () => {
                        (layer as GeoJSON).setStyle({ weight: 3, fillOpacity: 0.7 });
                    });
                    layer.on('mouseout', () => {
                        (layer as GeoJSON).setStyle({ weight: 1, fillOpacity});
                    });

                    const tooltip = new AreaTooltip({
                        propsData: {
                            name: area.name,
                            amount: area.price !== null ? area.price.amount : null,
                            currency: area.price !== null ? area.price.currency : null,
                        },
                        parent: this,
                    });

                    layer.bindTooltip(tooltip.$mount().$el as HTMLElement, {
                        direction: 'bottom',
                        sticky: true,
                        offset: [15, 15],
                    });
                },
            } as GeoJSONOptions;
        },

        estateTypeChanged(newEstateType: string) {
            this.estateType = newEstateType;
            this.reload();
        },

        currencyChanged(newCurrency: string) {
            this.currency = newCurrency;
            this.reload();
        },
    },
});
